import {
  passwordPolicyQuery,
  usePasswordPolicy,
} from "@common/api/password-policy"
import { useResetPasswordMutation } from "@common/api/users"
import type { NewPassword } from "@common/client"
import { PasswordRequirements } from "@common/components/password-requirements"
import { Button } from "@common/components/ui/button"
import { Input } from "@common/components/ui/input"
import { Label } from "@common/components/ui/label"
import { isLoggedIn } from "@common/hooks/use-auth"
import { queryClient } from "@common/lib/query-client"
import { confirmPasswordRules, handleError, passwordRules } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import useCustomToast from "../hooks/use-custom-toast"

interface NewPasswordForm extends NewPassword {
  confirm_password: string
}

export const Route = createFileRoute("/reset-password")({
  component: ResetPassword,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
    await queryClient.ensureQueryData(passwordPolicyQuery())
  },
})

function ResetPassword() {
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      new_password: "",
    },
  })

  const newPasswordValue = watch("new_password") ?? ""
  const showToast = useCustomToast()
  const navigate = useNavigate()
  const mutation = useResetPasswordMutation()
  const policy = usePasswordPolicy()

  const onSubmit: SubmitHandler<NewPasswordForm> = async (data) => {
    const token = new URLSearchParams(window.location.search).get("token")
    if (!token) return

    mutation.mutate(
      { newPassword: { new_password: data.new_password, token } },
      {
        onSuccess: () => {
          showToast("Success!", "Password updated successfully.", "success")
          reset()
          navigate({ to: "/login" })
        },
        onError: (err) => {
          handleError(err, showToast)
        },
      },
    )
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="text-center space-y-2">
            <h1 className="text-h1 text-primary">Reset Password</h1>
            <p className="text-muted-foreground">
              Please enter your new password and confirm it to reset your
              password.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Set Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Password"
              {...register("new_password", passwordRules(policy))}
            />
            {errors.new_password && (
              <p className="text-small-medium text-destructive">
                {errors.new_password.message}
              </p>
            )}
            <PasswordRequirements policy={policy} password={newPasswordValue} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm Password</Label>
            <Input
              id="confirm_password"
              type="password"
              placeholder="Repeat Password"
              {...register("confirm_password", confirmPasswordRules(getValues))}
            />
            {errors.confirm_password && (
              <p className="text-small-medium text-destructive">
                {errors.confirm_password.message}
              </p>
            )}
          </div>

          <Button className="w-full" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Resetting…" : "Reset Password"}
          </Button>
        </form>
      </div>
    </div>
  )
}
